import type { ReactElement } from 'react';
import { Route } from 'react-router-dom';
import { AuthGuard, useAuth, type RoleName } from '@/features/auth';
import { CompaniesPage, RefereeSettingsPage } from '@/features/companies';
import { CompanyAdminDashboardPage, CompanyUsersPage } from '@/features/company-admin';
import { SuperAdminsPage } from '@/features/super-admins';
import { VerificationInspectionPage } from '@/features/super-admin-inspection';
import {
  SystemQuestionsSettingsPage,
  CompanyQuestionsSettingsPage,
} from '@/features/referee-questions';
import { SettingsPage, CompanySettingsPage } from '@/features/settings';
import { CompanyProfileSettingsPage } from '@/features/companies';
import { DashboardPage } from '@/features/dashboard';
import { AgentDashboardPage } from '@/features/agent';
import { JobPositionsPage, JobPositionDetailPage } from '@/features/job-positions';
import { CandidatesPage, CandidateDetailPage, CandidateNewPage } from '@/features/candidates';
import {
  CandidateAppLayout,
  AnonymousCandidateWrapper,
  CandidateSessionProvider,
  CandidateRegistrationPage,
  CandidateDashboardPage,
} from '@/features/candidate-app';
import { AnonymousRefereeWrapper, RefereeDashboardPage } from '@/features/referee-app';
import { CertificatePage } from '@/features/candidate-certificate';

const SUPER_ADMIN_ONLY = ['SUPER_ADMIN'] as const;
const ADMIN_AND_ABOVE = ['SUPER_ADMIN', 'ADMIN'] as const;
const ALL_COMPANY_ROLES = ['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'AGENT'] as const;
const COMPANY_SCOPED = ['AGENT', 'MANAGER', 'ADMIN'] as const;

type RoleList = readonly RoleName[];

function guarded(path: string, roles: RoleList, element: ReactElement): ReactElement {
  return (
    <Route path={path} element={<AuthGuard requiredRoles={[...roles]}>{element}</AuthGuard>} />
  );
}

export function PlaceholderPage({ title }: { title: string }): ReactElement {
  return (
    <div className="container py-6">
      <h1 className="text-2xl font-bold">{title}</h1>
      <p className="text-muted-foreground">Coming soon...</p>
    </div>
  );
}

export function DashboardPageWrapper(): ReactElement {
  const { user } = useAuth();
  const isSuperAdmin = user?.roles?.includes('SUPER_ADMIN') ?? false;
  return <DashboardPage userName={user?.profile.fullName} isSuperAdmin={isSuperAdmin} />;
}

export function CompaniesPageWrapper(): ReactElement {
  const { user } = useAuth();
  const isSuperAdmin = user?.roles?.includes('SUPER_ADMIN') ?? false;
  return <CompaniesPage isSuperAdmin={isSuperAdmin} />;
}

export function SuperAdminsPageWrapper(): ReactElement {
  const { user } = useAuth();
  return <SuperAdminsPage currentUserId={user?.id} />;
}

export function SuperAdminRoutes(): ReactElement {
  return (
    <>
      {guarded('/companies', SUPER_ADMIN_ONLY, <CompaniesPageWrapper />)}
      {guarded('/users/super-admins', SUPER_ADMIN_ONLY, <SuperAdminsPageWrapper />)}
      {guarded('/verifications/inspection', SUPER_ADMIN_ONLY, <VerificationInspectionPage />)}
      {guarded('/settings', SUPER_ADMIN_ONLY, <SettingsPage />)}
      {guarded('/settings/referee-questions', SUPER_ADMIN_ONLY, <SystemQuestionsSettingsPage />)}
    </>
  );
}

export function CompanyRoutes(): ReactElement {
  return (
    <>
      {guarded('/companies/:id/dashboard', ADMIN_AND_ABOVE, <CompanyAdminDashboardPage />)}
      {guarded('/companies/:id/users', ADMIN_AND_ABOVE, <CompanyUsersPage />)}
      {guarded('/companies/:id/candidates', ALL_COMPANY_ROLES, <CandidatesPage />)}
      {guarded('/companies/:id/positions', ALL_COMPANY_ROLES, <JobPositionsPage />)}
      {guarded('/companies/:companyId/settings', ADMIN_AND_ABOVE, <CompanySettingsPage />)}
      {guarded(
        '/companies/:companyId/settings/profile',
        ADMIN_AND_ABOVE,
        <CompanyProfileSettingsPage />
      )}
      {guarded(
        '/companies/:companyId/settings/referee-questions',
        ADMIN_AND_ABOVE,
        <CompanyQuestionsSettingsPage />
      )}
      {guarded('/companies/:companyId/settings/referee', ADMIN_AND_ABOVE, <RefereeSettingsPage />)}
    </>
  );
}

export function AgentRoutes(): ReactElement {
  return (
    <>
      {guarded('/agent/dashboard', COMPANY_SCOPED, <AgentDashboardPage />)}
      {guarded('/agent/users', ['ADMIN'], <PlaceholderPage title="Users" />)}
      {guarded('/agent/candidates', COMPANY_SCOPED, <CandidatesPage />)}
      {guarded('/candidates/new', COMPANY_SCOPED, <CandidateNewPage />)}
      {guarded('/candidates/:id', ALL_COMPANY_ROLES, <CandidateDetailPage />)}
      {guarded('/positions', COMPANY_SCOPED, <JobPositionsPage />)}
      {guarded('/positions/:id', COMPANY_SCOPED, <JobPositionDetailPage />)}
      {guarded('/agent/settings', ['ADMIN'], <PlaceholderPage title="Agent Settings" />)}
    </>
  );
}

export function CandidatePublicRoutes(): ReactElement {
  return (
    <>
      <Route path="/candidate/:shareId" element={<AnonymousCandidateWrapper />}>
        <Route index element={<CandidateDashboardPage />} />
        <Route path="register" element={<CandidateRegistrationPage />} />
      </Route>
    </>
  );
}

export function CertificatePublicRoutes(): ReactElement {
  return <Route path="/certificate/:shareId" element={<CertificatePage />} />;
}

export function RefereePublicRoutes(): ReactElement {
  return (
    <>
      <Route path="/referee/:shareId" element={<AnonymousRefereeWrapper />}>
        <Route index element={<RefereeDashboardPage />} />
      </Route>
    </>
  );
}

export function CandidateAuthenticatedRoutes(): ReactElement {
  return (
    <Route
      element={
        <AuthGuard requiredRoles={['CANDIDATE']}>
          <CandidateSessionProvider mode="authenticated">
            <CandidateAppLayout />
          </CandidateSessionProvider>
        </AuthGuard>
      }
    >
      <Route path="/candidate/dashboard" element={<CandidateDashboardPage />} />
    </Route>
  );
}
